import { useId, useRef } from 'react'
import ReactDOM from 'react-dom'
import CloseIconButton, { DARK_APPEARANCE } from './CloseIconButton'
import useDialogFocus from './useDialogFocus'

// Shared confirm/notice shell. Pipeline.jsx's delete-property flow is the original, single-purpose
// caller and every prop below defaults to exactly what it already renders -- the Templates system's
// three popups (Overwrite/Delete template/Template can't be deleted, mr-6 spec S9) are the reason
// title/width/panelBg/confirmClassName/cancelLabel exist, passed explicitly by that caller. See
// src/components/README.md ("ConfirmModal is one shell for four popups") for why these three were
// generalised here rather than built as bespoke shells.
// Not "Close": DeleteBlockedModal's own confirm button already carries that name.
const CLOSE_LABEL = 'Close dialog'

// `frame` switches to the layout the SOW 3 Templates popups share (save-popup,
// delete-confirmation-popup, the two error popups): left-aligned copy, a 16px dark X at 20/20 and a
// fixed-width button pinned to the bottom of a fixed-height panel. Values: src/components/README.md.
// The X takes the template pop-up's #328C12 hover (Cong, letter 111, Comment Set #3 item 1).
function FramedPanel({ onClose, onConfirm, isLoading, error, title, body, confirmLabel, loadingLabel, confirmClassName, cancelLabel, onCancel, width, panelBg, frame, dialogProps, titleId, bodyId, panelRef, cancelRef }) {
    const { minHeight, padX, padTop, padBottom, titleSize, titleLeading, titleGap, buttonWidth } = frame
    const buttonStyle = { width: buttonWidth }
    return (
        <div
            className="fixed inset-0 z-[150] bg-black/20 flex items-center justify-center"
            onClick={onClose}
        >
            <div
                ref={panelRef}
                {...dialogProps}
                className="relative max-w-[calc(100vw-32px)] rounded-[16px] flex flex-col"
                style={{ width, minHeight, background: panelBg, padding: `${padTop}px ${padX}px ${padBottom}px` }}
                onClick={e => e.stopPropagation()}
            >
                <CloseIconButton
                    onClick={onClose}
                    ariaLabel={CLOSE_LABEL}
                    span={16}
                    stroke={1}
                    appearance={DARK_APPEARANCE}
                    className="absolute top-[16px] right-[16px]"
                />

                {title && (
                    <h2
                        id={titleId}
                        className="font-extrabold text-brand-dark"
                        style={{ fontSize: titleSize, lineHeight: `${titleLeading}px`, marginBottom: titleGap }}
                    >
                        {title}
                    </h2>
                )}

                {/* flex-1 pins the buttons to the panel bottom, as every frame draws them; mb is the
                    smallest body-to-button gap drawn (the 4-line error popups). */}
                <p id={bodyId} className="flex-1 mb-[16px] text-[14px] leading-[20px] text-brand-dark">{body}</p>

                <div className="flex items-center justify-center gap-x-[12px]">
                    {cancelLabel && (
                        <button
                            ref={cancelRef}
                            onClick={onCancel}
                            disabled={isLoading}
                            style={buttonStyle}
                            className="h-[40px] rounded-[20px] border border-brand-fieldBdr text-brand-dark text-[14px] font-semibold hover:border-brand-hover hover:text-brand-hover disabled:opacity-60"
                        >
                            {cancelLabel}
                        </button>
                    )}
                    <button
                        onClick={onConfirm}
                        disabled={isLoading}
                        style={buttonStyle}
                        className={`h-[40px] rounded-[20px] text-white text-[14px] font-semibold disabled:opacity-60 ${confirmClassName}`}
                    >
                        {isLoading ? loadingLabel : confirmLabel}
                    </button>
                </div>

                {error && (
                    <p className="mt-3 text-[12px] text-red-500 text-center">{error}</p>
                )}
            </div>
        </div>
    )
}

function ConfirmModal({
    open,
    onClose,
    onConfirm,
    isLoading,
    error,
    title,
    body = 'Are you sure you want to delete?',
    confirmLabel = 'Delete',
    loadingLabel = 'Deleting…',
    confirmClassName = 'bg-[#D85263] hover:bg-[#E86575]',
    cancelLabel,
    onCancel,
    width = 360,
    panelBg = '#f8fef4',
    frame,
}) {
    const panelRef = useRef(null)
    const cancelRef = useRef(null)
    const titleId = useId()
    const bodyId = useId()
    // Escape does what the X does, and nothing while the request runs (Cancel is disabled then too).
    // Focus starts on Cancel when there is one: the safe choice on a destructive confirm.
    useDialogFocus({ open, panelRef, initialFocusRef: cancelLabel ? cancelRef : undefined, onEscape: isLoading ? undefined : onClose })

    if (!open) return null;
    // Pipeline's default call has no title, so its one sentence names the dialog.
    const dialogProps = {
        role: 'dialog',
        'aria-modal': 'true',
        'aria-labelledby': title ? titleId : bodyId,
        'aria-describedby': title ? bodyId : undefined,
    }
    if (frame) {
        return ReactDOM.createPortal(
            <FramedPanel {...{ onClose, onConfirm, isLoading, error, title, body, confirmLabel, loadingLabel, confirmClassName, cancelLabel, onCancel, width, panelBg, frame, dialogProps, titleId, bodyId, panelRef, cancelRef }} />,
            document.getElementById('portal')
        )
    }

    return ReactDOM.createPortal(
        <div
            className="fixed inset-0 z-[150] bg-black/20 flex items-center justify-center"
            onClick={onClose}
        >
            <div
                ref={panelRef}
                {...dialogProps}
                className="relative max-w-[calc(100vw-32px)] rounded-[16px] px-8 pt-[72px] pb-[48px] flex flex-col items-center"
                style={{ width, background: panelBg }}
                onClick={e => e.stopPropagation()}
            >
                <CloseIconButton onClick={onClose} ariaLabel={CLOSE_LABEL} className="absolute top-[16px] right-[16px]" />

                {title && (
                    <h2 id={titleId} className="text-[20px] font-extrabold text-brand-dark mb-[12px] text-center">{title}</h2>
                )}

                <p id={bodyId} className="text-[14px] text-brand-dark mb-[32px] text-center">
                    {body}
                </p>

                <div className="flex items-center gap-x-[12px]">
                    {cancelLabel && (
                        <button
                            ref={cancelRef}
                            onClick={onCancel}
                            disabled={isLoading}
                            // Matches the reference "buttons-hover" board -- see README.md.
                            className="px-[24px] py-[8px] rounded-[48px] border border-brand-fieldBdr text-brand-dark text-[14px] font-semibold hover:border-brand-hover hover:text-brand-hover disabled:opacity-60"
                        >
                            {cancelLabel}
                        </button>
                    )}
                    <button
                        onClick={onConfirm}
                        disabled={isLoading}
                        className={`px-[24px] py-[8px] rounded-[48px] text-white text-[14px] font-semibold disabled:opacity-60 ${confirmClassName}`}
                    >
                        {isLoading ? loadingLabel : confirmLabel}
                    </button>
                </div>

                {error && (
                    <p className="mt-3 text-[12px] text-red-500 text-center">{error}</p>
                )}
            </div>
        </div>,
        document.getElementById('portal')
    )
}

export default ConfirmModal
